import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Alert, Spinner, Table } from 'react-bootstrap';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { apiService } from '../../services/api';

// 註冊 Chart.js 組件
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface ReportData {
  dailyRevenue: {
    labels: string[];
    data: number[];
  };
  monthlyRevenue: {
    labels: string[];
    data: number[];
  };
  parkingOccupancy: {
    labels: string[];
    data: number[];
  };
  vehicleTypes: {
    labels: string[];
    data: number[];
  };
  peakHours: {
    labels: string[];
    data: number[];
  };
  summary: {
    totalRevenue: number;
    totalVehicles: number;
    averageStayTime: string;
    occupancyRate: number;
  };
}

const Reports: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [reportType, setReportType] = useState<string>('daily');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // 載入報表數據
  const loadReportData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // 從API獲取報表數據
      const response = await apiService.getReportsDashboard();
      
      if (response.success && response.data) {
        const apiData = response.data;
        
        // 處理收入趨勢數據
        const revenueTrendLabels = apiData.revenueTrend?.map((item: any) => item._id) || [];
        const revenueTrendData = apiData.revenueTrend?.map((item: any) => item.revenue) || [];
        
        // 生成模擬的車輛類型數據
        const vehicleTypeLabels = ['小客車', '機車', 'VIP車輛', '其他'];
        const vehicleTypeData = [
          Math.floor(Math.random() * 100) + 50,
          Math.floor(Math.random() * 50) + 20,
          apiData.vehicles?.vip || 0,
          Math.floor(Math.random() * 30) + 10
        ];
        
        // 生成模擬的使用率數據
        const occupancyLabels = revenueTrendLabels.length > 0 ? revenueTrendLabels : ['今日'];
        const occupancyData = occupancyLabels.map(() => Math.floor(Math.random() * 40) + 60);
        
        // 將API數據轉換為前端需要的格式
        const reportData: ReportData = {
          dailyRevenue: {
            labels: revenueTrendLabels.length > 0 ? revenueTrendLabels : ['今日'],
            data: revenueTrendData.length > 0 ? revenueTrendData : [apiData.today?.revenue || 0]
          },
          monthlyRevenue: {
            labels: ['10月', '11月', '12月', '1月'],
            data: [35000, 42000, 38000, 45000] // 可以根據需要從API獲取
          },
          parkingOccupancy: {
            labels: occupancyLabels,
            data: occupancyData
          },
          vehicleTypes: {
            labels: vehicleTypeLabels,
            data: vehicleTypeData
          },
          peakHours: {
            labels: ['06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00'],
            data: [20, 45, 35, 60, 40, 55, 80, 65, 30]
          },
          summary: {
            totalRevenue: apiData.today?.revenue || 0,
            totalVehicles: apiData.today?.sessions || 0,
            averageStayTime: '3小時25分鐘', // 可以根據需要從API獲取
            occupancyRate: apiData.parking?.occupancyRate || 0
          }
        };
        
        setReportData(reportData);
      } else {
        throw new Error(response.message || '獲取報表數據失敗');
      }
    } catch (error: any) {
      console.error('載入報表數據失敗:', error);
      setError(`載入報表數據失敗: ${error.response?.data?.message || error.message || '請稍後再試'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReportData();
  }, [reportType, dateRange]);

  // 圖表配置
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right' as const,
      },
    },
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert variant="warning" className="text-center">
        <i className="fas fa-info-circle me-2"></i>
        沒有可用的報表數據
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <i className="fas fa-chart-bar me-3"></i>
          報表分析
        </h1>
        <Button variant="primary" onClick={loadReportData}>
          <i className="fas fa-sync-alt me-2"></i>
          重新整理
        </Button>
      </div>

      {/* 篩選條件 */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>報表類型</Form.Label>
                <Form.Select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                >
                  <option value="daily">日報表</option>
                  <option value="weekly">週報表</option>
                  <option value="monthly">月報表</option>
                  <option value="yearly">年報表</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>開始日期</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>結束日期</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button variant="outline-primary" className="w-100">
                <i className="fas fa-download me-2"></i>
                匯出
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 統計摘要 */}
      <Row className="mb-4">
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center h-100">
            <Card.Body>
              <i className="fas fa-dollar-sign fa-2x text-success mb-2"></i>
              <h3 className="text-success">NT$ {reportData.summary.totalRevenue.toLocaleString()}</h3>
              <p className="mb-0">總收入</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center h-100">
            <Card.Body>
              <i className="fas fa-car fa-2x text-primary mb-2"></i>
              <h3 className="text-primary">{reportData.summary.totalVehicles}</h3>
              <p className="mb-0">總車輛數</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center h-100">
            <Card.Body>
              <i className="fas fa-clock fa-2x text-info mb-2"></i>
              <h3 className="text-info">{reportData.summary.averageStayTime}</h3>
              <p className="mb-0">平均停車時間</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center h-100">
            <Card.Body>
              <i className="fas fa-percentage fa-2x text-warning mb-2"></i>
              <h3 className="text-warning">{reportData.summary.occupancyRate}%</h3>
              <p className="mb-0">平均使用率</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 圖表區域 */}
      <Row className="mb-4">
        <Col lg={8} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-line me-2"></i>
                收入趨勢
              </h5>
            </Card.Header>
            <Card.Body>
              <Bar
                data={{
                  labels: reportData.dailyRevenue.labels,
                  datasets: [
                    {
                      label: '每日收入 (NT$)',
                      data: reportData.dailyRevenue.data,
                      backgroundColor: 'rgba(54, 162, 235, 0.5)',
                      borderColor: 'rgba(54, 162, 235, 1)',
                      borderWidth: 1,
                    },
                  ],
                }}
                options={chartOptions}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-pie me-2"></i>
                車輛類型分布
              </h5>
            </Card.Header>
            <Card.Body>
              <Doughnut
                data={{
                  labels: reportData.vehicleTypes.labels,
                  datasets: [
                    {
                      data: reportData.vehicleTypes.data,
                      backgroundColor: [
                        'rgba(255, 99, 132, 0.8)',
                        'rgba(54, 162, 235, 0.8)',
                        'rgba(255, 205, 86, 0.8)',
                        'rgba(75, 192, 192, 0.8)',
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
                options={doughnutOptions}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-area me-2"></i>
                停車場使用率
              </h5>
            </Card.Header>
            <Card.Body>
              <Line
                data={{
                  labels: reportData.parkingOccupancy.labels,
                  datasets: [
                    {
                      label: '使用率 (%)',
                      data: reportData.parkingOccupancy.data,
                      borderColor: 'rgba(75, 192, 192, 1)',
                      backgroundColor: 'rgba(75, 192, 192, 0.2)',
                      tension: 0.4,
                      fill: true,
                    },
                  ],
                }}
                options={chartOptions}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-bar me-2"></i>
                尖峰時段分析
              </h5>
            </Card.Header>
            <Card.Body>
              <Bar
                data={{
                  labels: reportData.peakHours.labels,
                  datasets: [
                    {
                      label: '車輛數量',
                      data: reportData.peakHours.data,
                      backgroundColor: 'rgba(255, 159, 64, 0.5)',
                      borderColor: 'rgba(255, 159, 64, 1)',
                      borderWidth: 1,
                    },
                  ],
                }}
                options={chartOptions}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 詳細數據表格 */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <i className="fas fa-table me-2"></i>
            詳細數據
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive striped hover className="mb-0">
            <thead className="table-dark">
              <tr>
                <th>日期</th>
                <th>收入 (NT$)</th>
                <th>車輛數</th>
                <th>使用率 (%)</th>
                <th>平均停車時間</th>
              </tr>
            </thead>
            <tbody>
              {reportData.dailyRevenue.labels.map((date, index) => (
                <tr key={index}>
                  <td>{date}</td>
                  <td className="fw-bold text-success">
                    NT$ {reportData.dailyRevenue.data[index].toLocaleString()}
                  </td>
                  <td>{Math.floor(reportData.dailyRevenue.data[index] / 15)}</td>
                  <td>
                    <span className={`badge ${reportData.parkingOccupancy.data[index] > 80 ? 'bg-success' : reportData.parkingOccupancy.data[index] > 60 ? 'bg-warning' : 'bg-danger'}`}>
                      {reportData.parkingOccupancy.data[index]}%
                    </span>
                  </td>
                  <td>{Math.floor(Math.random() * 2) + 2}小時{Math.floor(Math.random() * 60)}分鐘</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Reports;